import React, { useState } from 'react';
import './IntakeForm.css';

interface FormData {
  name: string;
  email: string;
  phone: string;
  caseType: string;
  otherCaseType: string;
  description: string;
}

interface FieldErrors {
  name?: string;
  email?: string;
  phone?: string;
}

interface SubmitStatus {
  type: 'success' | 'error' | '';
  message: string;
}

interface IntakeFormProps {
  onOpenPrivacy: () => void;
}

function validateName(value: string): string | undefined {
  const words = value.trim().split(/\s+/).filter(w => w.length >= 2 && /^[A-Za-z'-]+$/.test(w));
  if (words.length < 2) return 'Please enter your first and last name.';
  return undefined;
}

function validateEmail(value: string): string | undefined {
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(value.trim())) {
    return 'Please enter a valid email address.';
  }
  return undefined;
}

function validatePhone(value: string): string | undefined {
  const digits = value.replace(/\D/g, '');
  if (digits.length !== 10) return 'Please enter a 10-digit US phone number.';
  if (parseInt(digits[0]) < 2) return 'Please enter a valid area code.';
  if (parseInt(digits[3]) < 2) return 'Please enter a valid phone number.';
  return undefined;
}

function IntakeForm({ onOpenPrivacy }: IntakeFormProps) {
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    phone: '',
    caseType: '',
    otherCaseType: '',
    description: '',
  });

  const [touched, setTouched] = useState<Partial<Record<keyof FormData, boolean>>>({});
  const [status, setStatus] = useState<SubmitStatus>({ type: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const errors: FieldErrors = {
    ...(formData.name ? { name: validateName(formData.name) } : {}),
    ...(formData.email ? { email: validateEmail(formData.email) } : {}),
    ...(formData.phone ? { phone: validatePhone(formData.phone) } : {}),
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    setTouched(prev => ({ ...prev, [e.target.name]: true }));
  };

  const isComplete =
    formData.name.trim() !== '' &&
    formData.email.trim() !== '' &&
    formData.phone.trim() !== '' &&
    formData.caseType !== '' &&
    (formData.caseType !== 'other' || formData.otherCaseType.trim() !== '') &&
    formData.description.trim() !== '';

  const isValid = isComplete &&
    !validateName(formData.name) &&
    !validateEmail(formData.email) &&
    !validatePhone(formData.phone);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    setStatus({ type: '', message: '' });

    try {
      const response = await fetch('/api/intake', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });

      const data: { success: boolean } = await response.json();

      if (data.success) {
        setStatus({
          type: 'success',
          message: 'Thank you for your submission. We will review your case and contact you soon.',
        });
        setFormData({ name: '', email: '', phone: '', caseType: '', otherCaseType: '', description: '' });
        setTouched({});
      } else {
        setStatus({
          type: 'error',
          message: 'There was an error submitting your form. Please try again.',
        });
      }
    } catch {
      setStatus({
        type: 'error',
        message: 'Unable to submit form. Please try again later or contact us directly.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="intake" className="intake-form-section">
      <div className="container">
        <h2>Request a Consultation</h2>
        <p className="intake-intro">
          Please provide information about your case. Intake submissions are used only
          to respond to your inquiry and are not retained in a website database.
          See our <button type="button" className="privacy-link" onClick={onOpenPrivacy}><strong>Privacy Policy</strong></button> for details.
        </p>

        {status.message && (
          <div className={`alert alert-${status.type}`}>
            {status.message}
          </div>
        )}

        <form onSubmit={handleSubmit} className="intake-form">
          <p className="intake-required-note">All fields are required.</p>
          <div className="form-group">
            <label htmlFor="name">Name</label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              onBlur={handleBlur}
              required
              placeholder="Alex Smith"
              className={touched.name && errors.name ? 'input-error' : ''}
            />
            {touched.name && errors.name && <span className="field-error">{errors.name}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="email">Email Address</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              onBlur={handleBlur}
              required
              placeholder="alex@example.com"
              className={touched.email && errors.email ? 'input-error' : ''}
            />
            {touched.email && errors.email && <span className="field-error">{errors.email}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="phone">Phone Number</label>
            <input
              type="tel"
              id="phone"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              onBlur={handleBlur}
              required
              placeholder="(555) 555-5555"
              className={touched.phone && errors.phone ? 'input-error' : ''}
            />
            {touched.phone && errors.phone && <span className="field-error">{errors.phone}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="caseType">Type of Case</label>
            <select
              id="caseType"
              name="caseType"
              value={formData.caseType}
              onChange={handleChange}
              required
            >
              <option value="">Select a case type...</option>
              <option value="privacy">Privacy Law</option>
              <option value="data-breach">Data Breach / Incident Response</option>
              <option value="employment">Employment Dispute</option>
              <option value="wrongful-termination">Wrongful Termination</option>
              <option value="discrimination">Discrimination</option>
              <option value="harassment">Harassment</option>
              <option value="other">Other</option>
            </select>
          </div>

          {formData.caseType === 'other' && (
            <div className="form-group form-group--other-fade">
              <label htmlFor="otherCaseType">Please describe your case type</label>
              <input
                type="text"
                id="otherCaseType"
                name="otherCaseType"
                value={formData.otherCaseType}
                onChange={handleChange}
                required
                placeholder="Brief description of case type"
              />
            </div>
          )}

          <div className="form-group">
            <label htmlFor="description">Case Description</label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              required
              rows={6}
              placeholder="Please provide details about your case..."
            />
          </div>

          <button type="submit" className="btn btn-submit" disabled={isSubmitting || !isValid}>
            {isSubmitting ? 'Submitting...' : 'Submit Request'}
          </button>
        </form>
      </div>
    </section>
  );
}

export default IntakeForm;
